/**
 * Records which paragraphs Word reports changed or added, with a timestamp,
 * so recovery can check recently edited paragraphs first and the server can
 * be told what the user touched. One ledger serves all trackers for the life
 * of the add-in; it subscribes to the document's event stream lazily and never
 * holds more than `ledgerLimit` ids.
 */
import type { DocumentEvent } from "./document-events";
import type { RecoveryConfig } from "./recovery-config";
import type { WordDocument } from "./word-document";

export class DocumentChangeLedger {
    private lastTouchedAtById = new Map<string, number>();
    private subscribedToDocumentEvents = false;

    constructor(
        private readonly word: WordDocument,
        private readonly config: RecoveryConfig,
    ) {}

    ensureSubscribed(): void {
        /**
         * WHEN the ledger already follows the document's event stream
         *   CONSEQUENCE
         *     Return now so this ledger keeps one listener.
         */
        if (this.subscribedToDocumentEvents) return;
        this.subscribedToDocumentEvents = true;
        const record = (uniqueLocalIds: string[]): void => {
            const now = Date.now();
            for (const id of uniqueLocalIds) this.lastTouchedAtById.set(id, now);
            /**
             * WHEN this batch pushes the ledger past `ledgerLimit`
             *   CONSEQUENCE
             *     Trim after recording the new touches, so the newest ids from this batch
             *     are eligible to stay and the oldest ids are evicted.
             */
            if (this.lastTouchedAtById.size > this.config.ledgerLimit) {
                /**
                 * EXAMPLE
                 *   size                     = 12
                 *   ledgerLimit              = 10
                 *   size - ledgerLimit       = 2
                 *   slice(0, 2)              -> entries at indexes 0 and 1
                 *   returned entries         -> the two oldest ids to delete
                 */
                const oldest = Array.from(this.lastTouchedAtById.entries())
                    .sort((left, right) => left[1] - right[1])
                    .slice(0, this.lastTouchedAtById.size - this.config.ledgerLimit);
                for (const [id] of oldest) this.lastTouchedAtById.delete(id);
            }
        };
        this.word.events.subscribe((event: DocumentEvent) => {
            /**
             * CASES
             *   - a body replacement begins          -> forget every id: the replaced body takes them
             *                                           with it, and the stream re-registers with Word
             *                                           so the restored body's edits are recorded from
             *                                           the first keystroke
             *   - Word reports paragraphs deleted    -> skip: those ids no longer name recoverable paragraphs
             *   - Word reports a content control     -> skip: the event names no paragraph
             *   - Word reports paragraphs changed
             *     or added                           -> record the touched ids
             *   - anything else (the typing detector,
             *     the end of a replacement)          -> nothing to record
             */
            if (event.kind === "body_replaced" && event.phase === "begin") {
                this.lastTouchedAtById.clear();
                return;
            }
            if (event.kind !== "word_change") return;
            if (event.change.kind === "deleted" || event.change.kind === "content_control_added") return;
            record(event.change.uniqueLocalIds);
        });
    }

    /** Ids touched at or after `timestampMs`, most recently touched first. */
    touchedSince(timestampMs: number): string[] {
        /**
         * TYPE
         *   Each entry is [id, lastTouchedAt].
         *
         * EXAMPLE
         *   left           = ["p7", 1000]
         *   right          = ["p9", 1500]
         *   right[1]       = 1500
         *   left[1]        = 1000
         *   1500 - 1000    -> positive for newer-right vs older-left ordering
         *   sort result    -> ["p9", 1500] comes first as the newer touch
         */
        return Array.from(this.lastTouchedAtById.entries())
            .filter(([, touchedAt]) => touchedAt >= timestampMs)
            .sort((left, right) => right[1] - left[1])
            .map(([id]) => id);
    }
}
